import React, {useEffect, useMemo, useRef, useState} from 'react';
import {noop} from 'lodash';
import {
	EconomicProduct,
	ExportInvoiceModalProps,
	InvoiceEntry,
	InvoiceEntryRow,
	OnErrorType,
	QuickbookProductService,
	QuickbookSetting,
	QuickbooksSettingId,
	TaxCode,
	XeroAccountItem,
} from './ExportInvoiceModal.type';
import {
	Alert,
	ButtonDropdown,
	ButtonDropdownField,
	Checkbox,
	ComboBox,
	DropdownList,
	FlexColumn,
	FlexRow,
	Heading,
	IconTooltip,
	Label,
	Modal,
	SubHeading,
	Table,
	Text,
	Tooltip,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import XeroExportLinesTable from './XeroExportLinesTable';
import XeroNothingImportedAlert, {XERO_CONFIGURATION_PATH} from './XeroNothingImportedAlert';
import {ExportHeaderRow} from './ExportInvoiceModal.styled';
import {
	applyXeroLineRows,
	areXeroListsLoaded,
	cachedXeroLineConfig,
	carryXeroSelections,
	EMPTY_LINE_CONFIG,
	FAILED_LINE_CONFIG,
	fetchXeroContacts,
	fetchXeroLineConfig,
	everyLineHasActiveAccount,
	hasUnresolvableAccount,
	isConfigStaleForClient,
	isNothingImported,
	isXeroContactMissing,
	isXeroExportBlocked,
	nextLinesOnTenantLoad,
	nextTenantId,
	selectedTenantFor,
	shouldUseXeroLinesTable,
	toXeroLineRows,
	withContactTaxRate,
	withDefaultTax,
	withOrganisationLabels,
	withRestatedMoney,
	XeroContact,
	xeroContactFor,
	XeroLineConfig,
} from './ExportInvoiceModal.xeroLines';
import {createFragmentContainer} from 'react-relay';
import {exportInvoiceModalFragment} from './ExportInvoiceModal.query';
import Util from '../../../forecast-app/shared/util/util';
import {getNotNullishNodes} from '../../../forecast-app/shared/util/NotNullPredicate';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {fetchXeroAccounts, onExportXeroInvoice} from './ExportInvoiceModal.xero';
import {hideLoader, showLoader} from '../../global_loader';
import {INVOICE_INTEGRATIONS} from '../../../forecast-app/admin-tab/finance-page/InvoiceIntegrationsUtils';
import {
	exportInvoiceToQuickbooksOnline,
	fetchQuickbooksData,
	formatQuickbooksLineTax,
	getInitialQuickbookSettings,
	getQuickbooksLineDescription,
	hasImportedTaxCodes,
	isChangedFromDefault,
	isQuickbooksTaxCodeMissing,
	isQuickbookSettingOn,
	QuickbooksClassAssignment,
	QuickbooksClassData,
	QuickbooksClassOption,
	quickbooksLinePrice,
	quickbooksLineTax,
	quickbooksLineTaxHover,
	resolveQuickbooksAutomatedSalesTax,
	resolveQuickbooksCustomerDefault,
	resolveQuickbookSettingDefaults,
	splitLastWord,
	toggleQuickbookSetting,
} from './ExportInvoiceModal.quickbooks';
import {ChangedFromDefaultDot, CompanyDefaultHint, ResetToCompanyDefaultsButton} from './ExportDefaultIndicator';
import {
	isQboInvoicingImprovementsEnabled,
	isXeroInvoicingImprovementsEnabled,
	isXeroInvoicingImprovementsEnabledOrOff,
} from '../../../forecast-app/shared/util/FeatureUtil';
import ExportDefaultTaxRateField from './ExportDefaultTaxRateField';
import {appliedDefaultTax, billToDefaultTaxRate, XeroDefaultTaxRate} from './ExportDefaultTaxRate.display';
import {
	xeroExportDefaultsFor,
	xeroExportDefaultsOf,
} from '../../../forecast-app/admin-tab/integrations-page/export-defaults/xeroExportDefaults';
import {exportInvoiceToEconomics, fetchEconomicProducts} from './ExportInvoiceModal.economic';
import {ExportTarget, getTargetIntegration, XeroClientOption, XeroExportTarget} from './ExportInvoiceModal.target';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';

// The legacy window's Client heading, which names its search field. One export window is open at a time.
const CLIENT_LABEL_ID = 'export-invoice-client-label';

const ExportInvoiceModal = ({closeModal, viewer, quickbooksCustomers, xeroClients}: ExportInvoiceModalProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const history = useHistory();

	const noSpace = true;

	const {invoice, billTo} = viewer;
	const {client, currency} = invoice || {};

	const currencySymbol = useMemo(() => Util.GetCurrencySymbol(currency), [invoice?.currency]);

	// The legacy window's Client options: QuickBooks- or Xero-linked clients, whichever path opened it.
	const clientOptions: {value: string; label: string}[] | undefined = quickbooksCustomers ?? xeroClients;
	// The legacy window preselects the invoice's client when it is linked - which is exactly when it
	// is in this list, since ExportInvoiceButton builds it from linked clients.
	const [selectedClientId, setSelectedClientId] = useState<string | null>(
		() => clientOptions?.find(option => option.value === invoice?.client?.id)?.value ?? null
	);
	const selectedClient = quickbooksCustomers?.find(option => option.value === selectedClientId) ?? null;
	const selectedXeroClient: XeroClientOption | null = xeroClients?.find(option => option.value === selectedClientId) ?? null;
	// What xero/contacts answered on the legacy Xero path: null until it has, and on every other path.
	const [xeroContacts, setXeroContacts] = useState<XeroContact[] | null>(null);
	// The legacy window's Client options, each Xero client labelled with its organisation once known.
	const clientComboItems = useMemo(
		() => (xeroClients ? withOrganisationLabels(xeroClients, xeroContacts) : clientOptions ?? []),
		[xeroClients, xeroContacts, quickbooksCustomers]
	);

	const exportTarget: ExportTarget | null = useMemo(() => {
		if (billTo) return {kind: 'BILL_TO', billTo};
		if (quickbooksCustomers) {
			return {
				kind: 'CLIENT',
				integration: INVOICE_INTEGRATIONS.QUICKBOOKS,
				clientId: selectedClient?.value ?? null,
				realmId: selectedClient?.realmId ?? null,
			};
		}
		if (xeroClients) {
			return {
				kind: 'CLIENT',
				integration: INVOICE_INTEGRATIONS.XERO,
				clientId: selectedXeroClient?.value ?? null,
				xeroContactId: selectedXeroClient?.xeroContactId ?? null,
				// The organisation is the contact's, so it is only known once xero/contacts has answered.
				tenantId: selectedTenantFor(xeroContacts ?? undefined, selectedXeroClient ?? undefined) ?? null,
			};
		}
		return null;
	}, [billTo, quickbooksCustomers, selectedClient, xeroClients, selectedXeroClient, xeroContacts]);
	const targetIntegration = getTargetIntegration(exportTarget);
	const xeroClientTarget =
		exportTarget?.kind === 'CLIENT' && exportTarget.integration === INVOICE_INTEGRATIONS.XERO ? exportTarget : null;

	const mapInvoiceEntryToRow = (
		invoiceEntry: InvoiceEntry,
		xeroAccount?: XeroAccountItem | null,
		quickbookProductService?: QuickbookProductService | null,
		economicProduct?: EconomicProduct | null
	): InvoiceEntryRow => {
		const taxRate = xeroAccount?.taxRate || 0;

		const row: InvoiceEntryRow = {
			...invoiceEntry,
			// The entry's own price, for restoring the line when a product is cleared.
			originalUnitPrice: invoiceEntry.unitPrice,
			projectColor: invoiceEntry.project?.projectColor || '',
			projectId: `P${invoiceEntry.project?.companyProjectId}`,
			customProjectId: invoiceEntry.project?.customProjectId,
			entryDescription: invoiceEntry.description,
			discountPercentage: (invoiceEntry.discount || 0) / 100,
			tax: taxRate,
			taxPercentage: taxRate / 100,
			totalPrice: Util.getInvoiceEntryAmount(
				invoiceEntry.unitPrice,
				invoiceEntry.quantity,
				invoiceEntry.discount,
				taxRate
			),
		};

		if (xeroAccount) {
			row.label = xeroAccount.label;
			row.xeroAccount = xeroAccount.value;
			row.accountCode = xeroAccount.code;
		}

		if (quickbookProductService) {
			row.label = quickbookProductService.label;
			// Kept beside the line's own description, never over it (T4-156): the description column
			// previews what QuickBooks will receive, from the settings (tableRows).
			row.quickbooksDescription = quickbookProductService.description;
			row.quickbooksItem = quickbookProductService.value;
			row.quickbooksTaxable = quickbookProductService.taxable;
			row.quickbooksUnitPrice = quickbookProductService?.unitPrice;
		}

		if (economicProduct) {
			row.label = economicProduct.label;
			row.economicProduct = economicProduct.value;
		}

		return row;
	};

	const [loading, setLoading] = useState<boolean>(true);
	const [isReadyForExport, setIsReadyForExport] = useState<boolean>(false);

	useEffect(() => {
		if (loading) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [loading]);

	// INTEGRATION
	const isXeroInvoice = useMemo(() => targetIntegration === INVOICE_INTEGRATIONS.XERO, [targetIntegration]);

	/*
	 * Resolved once per render. The ticket asks for the check "at the point where the invoice line
	 * decides which path to call"; scattering the call made it four separate decisions.
	 */
	const useLinesTable = shouldUseXeroLinesTable(isXeroInvoice);
	/*
	 * T4-197: the client's Xero default tax rate in the header. The guarded flag read: this field's own
	 * read never throws, and a broken flag cache reads as off for it - today's header, with nothing requested
	 * (the conductor sends the same flag as the query's @include).
	 */
	const showDefaultTaxRate = isXeroInvoice && isXeroInvoicingImprovementsEnabledOrOff();
	const isQuickbooksInvoice = useMemo(() => targetIntegration === INVOICE_INTEGRATIONS.QUICKBOOKS, [targetIntegration]);
	/*
	 * T4-149's new QuickBooks body, read once per render like useLinesTable: two reads in one
	 * interaction could disagree. Off, the pathways window is exactly today's, except the Description
	 * column, which previews what QuickBooks will receive with the flag on or off (T4-156).
	 */
	const qboNewBody = isQuickbooksInvoice && isQboInvoicingImprovementsEnabled();
	const isEconomicInvoice = useMemo(() => targetIntegration === INVOICE_INTEGRATIONS.ECONOMIC, [targetIntegration]);

	// INVOICE
	const initialInvoiceEntries = getNotNullishNodes(invoice?.entries?.edges);
	const [invoiceRows, setInvoiceRows] = useState<InvoiceEntryRow[]>(
		() => initialInvoiceEntries.map(entry => mapInvoiceEntryToRow(entry)) || []
	);

	// XERO
	/*
	 * With the Xero improvements flag on, the toggle opens at the default an Admin saved for the organisation
	 * the invoice is billed from (never saved is off). The opening value is kept for the window's life and
	 * never re-derived from viewer.billTo: an Admin's change while the window is open must not move the
	 * toggle, and a user's change is for this export only. It is only as fresh as the query the window
	 * mounted on - the conductor reads Xero windows network-only (see its EXPORT_INVOICE case).
	 * Which checkbox renders follows the opening value too (null: today's checkbox), so the window never
	 * switches between the two while it is open.
	 */
	const [openingProjectInDescription] = useState<boolean | null>(() =>
		isXeroInvoice && isXeroInvoicingImprovementsEnabled()
			? xeroExportDefaultsOf(billTo?.billFrom).projectInDescription
			: null
	);
	/*
	 * The legacy window has no bill-to: its organisation is the chosen client's, known only once xero/contacts
	 * has answered. The toggle opens at that organisation's saved default the first time one resolves and is
	 * never re-seeded after, so a later client change does not move it. Until then the box stays disabled.
	 */
	const [xeroClientDefaultsShown] = useState(() => !!xeroClients && isXeroInvoicingImprovementsEnabled());
	const [clientOpeningProjectInDescription, setClientOpeningProjectInDescription] = useState<boolean | null>(null);
	const xeroDefaultsShown = openingProjectInDescription !== null || xeroClientDefaultsShown;
	const [projectInDescription, setProjectInDescription] = useState<boolean>(() => openingProjectInDescription ?? false);
	// What the indicator and Reset compare against: the bill-from's default, or the client organisation's.
	const projectInDescriptionDefault = xeroClientDefaultsShown
		? clientOpeningProjectInDescription
		: openingProjectInDescription;
	const projectInDescriptionChanged =
		projectInDescriptionDefault !== null && projectInDescription !== projectInDescriptionDefault;
	const projectInDescriptionDisabled = loading || (xeroClientDefaultsShown && clientOpeningProjectInDescription === null);
	const projectInDescriptionRef = useRef<HTMLDivElement>(null);
	const [selectedXeroAccount, setSelectedXeroAccount] = useState<XeroAccountItem | null>(null);
	const [xeroAccounts, setXeroAccounts] = useState<XeroAccountItem[]>([]);
	const [xeroLineConfig, setXeroLineConfig] = useState<XeroLineConfig>(EMPTY_LINE_CONFIG);
	// The organisation the lists now showing were read for; none while they are empty placeholders.
	const [xeroLineConfigTenant, setXeroLineConfigTenant] = useState<string | undefined>(undefined);
	// Every change of the lists goes through here, so the lists and the organisation they belong to never drift.
	const showXeroLineConfig = (config: XeroLineConfig, tenantId?: string): void => {
		setXeroLineConfig(config);
		setXeroLineConfigTenant(tenantId);
	};
	const requestedTenant = useRef<string | undefined>(undefined);
	// The organisation the legacy Xero window's lines were last loaded for, to tell an organisation change apart.
	const lastTenant = useRef<string | null | undefined>(null);
	/*
	 * True from the start on the legacy Xero window: its configuration is read only once the contacts
	 * have named the client's organisation, and until then an empty configuration would read as
	 * "nothing imported". The contacts and client effects clear it.
	 */
	const [xeroLineConfigLoading, setXeroLineConfigLoading] = useState<boolean>(() => !!xeroClientTarget && useLinesTable);
	/*
	 * Distinct from `loading`, which also covers the initial fetches. The line table must stop
	 * accepting changes while an export is in flight - but it must not be frozen by
	 * fetchXeroAccounts, a request it does not use: that one feeds the invoice-level dropdown,
	 * which is hidden under the flag, and it has no catch, so a rejection would leave the table
	 * permanently inert.
	 */
	const [saving, setSaving] = useState<boolean>(false);
	/*
	 * Why the reason is rendered INSIDE this modal rather than pushed as a MODAL_TYPE.WARNING.
	 *
	 * This modal is a design-system `Modal`, which Radix portals to document.body with a z-index of
	 * 1000, sets `document.body.style.pointerEvents = 'none'` around, and `aria-hidden`s the rest of
	 * the tree under. The legacy warning renders inside that tree at z-index 900, so it is covered,
	 * unclickable and invisible to a screen reader - the user sees the spinner stop and nothing else.
	 *
	 * `onError` a few lines down has always known this: "Old error modal is shown behind new
	 * design-system modals - so we have to close it first". Closing is what the modal used to do on
	 * failure, and what this change deliberately stopped doing so the user keeps their picks. Keeping
	 * the modal open therefore means the reason has to come with it.
	 */
	const [exportError, setExportError] = useState<string | null>(null);

	// QUICKBOOKS
	const [quickbooksTaxCodes, setQuickbooksTaxCodes] = useState<TaxCode[]>([]);
	const [selectedQuickbooksCode, setSelectedQuickbooksCode] = useState<TaxCode | null>(null);
	const [quickbooksProductsServices, setQuickbooksProductsServices] = useState<QuickbookProductService[]>([]);
	const [selectedQuickbooksProductService, setSelectedQuickbooksProductService] = useState<QuickbookProductService | null>(
		null
	);
	/*
	 * Which list failed to load for the company now showing. A failed list is empty, and without these
	 * it would read like a company with nothing in it: "No tax", exported untaxed. Not gated on the
	 * flag - they apply to today's window too.
	 */
	const [quickbooksTaxCodesFailed, setQuickbooksTaxCodesFailed] = useState<boolean>(false);
	const [quickbooksProductsFailed, setQuickbooksProductsFailed] = useState<boolean>(false);
	/*
	 * Whether the lists now showing finished loading for the current client or Bill From. Empty lists
	 * alone do not say "nothing imported": they are also what the window holds before a client is
	 * picked and while a load is out.
	 */
	const [quickbooksListsLoaded, setQuickbooksListsLoaded] = useState<boolean>(false);
	/*
	 * The organisation's classes, and where the window offers one: in the header for the whole
	 * invoice, on each line, or not at all. OFF until the lists load, so the field never shows in the
	 * wrong place while they do. The header pick is the class id's option; a line's pick is on its row.
	 */
	const [quickbooksClasses, setQuickbooksClasses] = useState<QuickbooksClassOption[]>([]);
	const [quickbooksClassesFailed, setQuickbooksClassesFailed] = useState<boolean>(false);
	const [quickbooksClassAssignment, setQuickbooksClassAssignment] = useState<QuickbooksClassAssignment>('OFF');
	const [selectedQuickbooksClass, setSelectedQuickbooksClass] = useState<QuickbooksClassOption | null>(null);
	// T4-190 (spec 043 Decision 9): this load's own answer to "imported lists?", sent with the export. Not qboNewBody,
	// which re-reads the flag on every render.
	const [quickbooksImportedLists, setQuickbooksImportedLists] = useState(false);
	// T4-224 (spec 049 D6): a picked imported product prices its line, for every user who exports (FR20).
	const priceFromProduct = qboNewBody && quickbooksImportedLists;
	const [quickbookSettingsCount, setQuickbookSettingsCount] = useState<number>(0);
	/*
	 * T4-150: the company's export defaults as this window opened - null without the new body, so the
	 * options start off as before. Never re-derived from viewer.company afterwards: an Admin's change
	 * while the window is open must not move the options, and a user's change is for this export only.
	 * The initialisers are lazy only to skip the work on later renders; useState keeps its first value
	 * either way. The defaults are only as fresh as the query the window mounted on - the conductor
	 * reads QuickBooks windows network-only (see its EXPORT_INVOICE case) - so mount it only through
	 * the conductor.
	 */
	const [openingDefaults] = useState(() => (qboNewBody ? resolveQuickbookSettingDefaults(viewer.company) : null));
	const [quickbookSettings, setQuickbookSettings] = useState(() =>
		getInitialQuickbookSettings(intl, openingDefaults ?? undefined)
	);
	/*
	 * T4-151: which options differ from the defaults the window opened with, worked out on every render -
	 * the 2/3 rule can change two options in one click, and a reset or any later change is covered too.
	 * Never against viewer.company: the window keeps the values it opened with (T4-150), and the marks
	 * and the reset must use the same values.
	 */
	const anyChangedFromDefault = quickbookSettings.some(setting => isChangedFromDefault(setting, openingDefaults));
	const optionGridRef = useRef<HTMLDivElement>(null);
	const defaultTaxChecked = isQuickbookSettingOn(quickbookSettings, QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX);
	const customerDefaultTaxOn = qboNewBody && defaultTaxChecked;
	// With customer default tax on the code is not sent as the invoice's tax, so its list is not needed.
	const exportBlockedByTaxCodes = isQuickbooksInvoice && quickbooksTaxCodesFailed && !defaultTaxChecked;
	// A list that loaded with nothing imported for this organisation; a failed list says so instead.
	const quickbooksNoTaxCodesImported =
		qboNewBody && quickbooksListsLoaded && !quickbooksTaxCodesFailed && !hasImportedTaxCodes(quickbooksTaxCodes);
	// Export needs a tax code, "No tax" included, unless the customer's default applies.
	const quickbooksTaxCodeMissing =
		qboNewBody && isQuickbooksTaxCodeMissing({selected: selectedQuickbooksCode, customerDefaultTaxOn});
	const quickbooksNoProductsImported =
		qboNewBody && quickbooksListsLoaded && !quickbooksProductsFailed && quickbooksProductsServices.length === 0;
	// The header's first rule (T4-189): it wins over the toggle, and no code applies to the lines (spec 041 D5).
	const quickbooksNoTaxCodesShown = quickbooksNoTaxCodesImported && !selectedQuickbooksCode;

	/*
	 * T4-188 (spec 041 D4): the customer's stored default, when the export goes to that customer. The
	 * picker window reads it from the chosen client's option, the pathways window from the invoice's
	 * client. Display only: never put into selectedQuickbooksCode, so the request is unchanged (D6).
	 */
	const clientDefaultTaxRate =
		exportTarget?.kind === 'BILL_TO' ? invoice?.client?.qboDefaultTaxRate : selectedClient?.defaultTaxRate;
	const quickbooksCustomerDefault = useMemo(
		() =>
			qboNewBody
				? resolveQuickbooksCustomerDefault(
						exportTarget,
						clientDefaultTaxRate,
						invoice?.client?.quickbooksCustomerId ?? null
				  )
				: null,
		[qboNewBody, exportTarget, clientDefaultTaxRate, invoice?.client?.quickbooksCustomerId]
	);
	// D12: no default applies and the export's organisation uses automated sales tax.
	const quickbooksAutomatedSalesTax =
		customerDefaultTaxOn &&
		!quickbooksCustomerDefault &&
		resolveQuickbooksAutomatedSalesTax(exportTarget, viewer.company?.billFroms);
	// D7: the code the lines use - none while "No tax codes imported" shows, the default with the toggle on.
	const quickbooksAppliedCode = !qboNewBody
		? null
		: quickbooksNoTaxCodesShown
		? null
		: customerDefaultTaxOn
		? quickbooksCustomerDefault
		: selectedQuickbooksCode;

	// Class is optional: none of these blocks Export. The header field and every line say the same thing.
	const quickbooksClassesUnavailable = quickbooksClassesFailed || quickbooksClasses.length === 0;
	const quickbooksClassPlaceholder = formatMessage({
		id: quickbooksClassesFailed
			? 'invoicing.quickbooks.classes_load_failed'
			: quickbooksClasses.length === 0
			? 'invoicing.quickbooks.no_classes_imported'
			: 'invoicing.quickbooks.select_class',
	});
	const applyQuickbooksClassData = ({classes, classAssignment, importedLists}: QuickbooksClassData) => {
		setQuickbooksClasses(classes);
		setQuickbooksClassAssignment(classAssignment);
		setQuickbooksImportedLists(importedLists);
	};

	/*
	 * T4-198: the Xero default, judged once for the header and the lines so the two can't disagree
	 * (spec 045 D2). T4-222: on the Bill To path the Bill To's own default wins; with none, the
	 * client's counts only when the Bill To is the client's own Xero contact.
	 */
	const defaultTaxInput: {rate: XeroDefaultTaxRate | null | undefined; organisation: string | null | undefined} = useMemo(
		() =>
			xeroClients
				? {rate: selectedXeroClient?.xeroDefaultTaxRate, organisation: xeroClientTarget?.tenantId}
				: {
						rate: billToDefaultTaxRate(
							billTo?.xeroDefaultTaxRate,
							billTo?.externalId,
							client?.xeroContactId,
							client?.xeroDefaultTaxRate
						),
						organisation: billTo?.billFrom?.externalId,
				  },
		[xeroClients, selectedXeroClient, xeroClientTarget?.tenantId, billTo, client]
	);
	// Every line takes it when it applies, on the flag-on table only (spec 045 D1). Never stored on invoiceRows.
	const appliedDefault = useMemo(
		() =>
			useLinesTable && showDefaultTaxRate ? appliedDefaultTax(defaultTaxInput.rate, defaultTaxInput.organisation) : null,
		[useLinesTable, showDefaultTaxRate, defaultTaxInput]
	);

	/*
	 * Whether the line lists answered for the organisation the window now exports to: the chosen
	 * client's on the legacy window, the Bill From's on the pathways one. Until they have, empty lists
	 * say nothing about what the organisation imported.
	 */
	const xeroListsLoaded = areXeroListsLoaded(
		xeroLineConfigTenant,
		xeroClientTarget ? xeroClientTarget.tenantId : billTo?.billFrom?.externalId,
		xeroLineConfigLoading
	);
	const xeroNothingImported = useLinesTable && isNothingImported(xeroLineConfig, xeroListsLoaded);
	const xeroContactMissing =
		useLinesTable &&
		!!xeroClientTarget &&
		isXeroContactMissing(xeroContacts, xeroClientTarget.clientId, xeroClientTarget.tenantId);
	const openXeroConfiguration = (): void => {
		closeModal();
		// Pushed, not replaced, so Back returns to the invoice the window was opened from.
		history.push(XERO_CONFIGURATION_PATH);
	};

	/*
	 * Display only: the export sends entryMap and the tax code id, never descriptions or amounts, so
	 * this cannot change what is exported.
	 *
	 * Flag on (spec 041 D7-D9): every line takes the applied code's rate, except a US item stored as not
	 * taxable, which the export sends as NON (0%). The rate is a percentage (e.g. 5), the unit
	 * Util.getInvoiceEntryAmount and Util.getInvoiceTotals read `tax` in. Each row also carries the Tax
	 * cell's text and hover.
	 */
	// T4-224: only true once a line has a product, so the lists arriving (priceFromProduct turning on) does not
	// rebuild the rows on its own: nothing would change, and the rebuild would close a line menu just opened.
	const linePricesFromProduct = priceFromProduct && invoiceRows.some(row => !!row.quickbooksItem);
	const tableRows = useMemo(() => {
		if (!isQuickbooksInvoice) return withDefaultTax(invoiceRows, appliedDefault);
		return invoiceRows.map(row => {
			// With the flag on or off (T4-156): a picked product never hides the line's own description.
			const previewed = {...row, entryDescription: getQuickbooksLineDescription(row, quickbookSettings)};
			// Flag off, QuickBooks lines show no tax, as before.
			if (!qboNewBody) return previewed;
			const lineTax = quickbooksLineTax(row, quickbooksAppliedCode);
			const tax = lineTax.rate;
			// T4-224 (spec 049 D3): the shown copy only; the row keeps its Forecast price.
			const unitPrice = quickbooksLinePrice(row, linePricesFromProduct);
			return {
				...previewed,
				unitPrice,
				tax,
				taxPercentage: tax / 100,
				totalPrice: Util.getInvoiceEntryAmount(unitPrice, row.quantity, row.discount, tax),
				quickbooksTaxText: formatQuickbooksLineTax(intl, tax),
				quickbooksTaxHover: quickbooksLineTaxHover(intl, customerDefaultTaxOn, quickbooksAppliedCode, lineTax),
			};
		});
	}, [
		isQuickbooksInvoice,
		qboNewBody,
		linePricesFromProduct,
		invoiceRows,
		quickbookSettings,
		quickbooksAppliedCode,
		customerDefaultTaxOn,
		intl,
		appliedDefault,
	]);
	const invoiceTotals = useMemo(() => Util.getInvoiceTotals(tableRows), [tableRows]);

	// D5: placeholder style for "No default set" and the existing placeholders, never for a value shown.
	const quickbooksTaxFieldIsPlaceholder =
		quickbooksNoTaxCodesShown ||
		(customerDefaultTaxOn ? !quickbooksCustomerDefault && !quickbooksAutomatedSalesTax : !selectedQuickbooksCode);
	/*
	 * D5: the header hover, only while the toggle is on and "No tax codes imported" is not showing. The
	 * Tooltip's trigger is focusable, and the disabled field takes no pointer events, so it works on it.
	 */
	const renderQuickbooksTaxFieldHover = (field: JSX.Element): JSX.Element => {
		if (!customerDefaultTaxOn || quickbooksNoTaxCodesShown) return field;
		const id = quickbooksCustomerDefault
			? 'invoicing.quickbooks.default_tax_hover_set'
			: quickbooksAutomatedSalesTax
			? 'invoicing.quickbooks.default_tax_hover_ast'
			: 'invoicing.quickbooks.default_tax_hover_none';
		return (
			// The Tooltip's own non-interactable trigger is `width: fit-content`, which shrinks the field to its text;
			// `interactable` lets us give it our own full-width, focusable trigger. It only drops the Tooltip's
			// click preventDefault and pointer-down-outside guard, neither of which matters on a disabled field.
			<Tooltip content={formatMessage({id})} interactable>
				<div tabIndex={0} style={{width: '100%'}}>
					{field}
				</div>
			</Tooltip>
		);
	};
	/*
	 * D8: the QuickBooks Tax cell. TextColumn's own tooltip draws a help icon the design does not have, so
	 * the text is drawn through its icon slot, inside a Tooltip; a line with no hover is plain text.
	 */
	const renderQuickbooksLineTax = (row: InvoiceEntryRow): JSX.Element => {
		const text = (
			<Text size={'2'} noWrap>
				{row.quickbooksTaxText}
			</Text>
		);
		return row.quickbooksTaxHover ? (
			<Tooltip content={row.quickbooksTaxHover} interactable={false}>
				{text}
			</Tooltip>
		) : (
			text
		);
	};

	// E-CONOMIC
	const [economicProducts, setEconomicProducts] = useState<EconomicProduct[]>([]);
	const [selectedEconomicProduct, setSelectedEconomicProduct] = useState<EconomicProduct | null>(null);

	const dropdownColumnLabel = useMemo(() => {
		if (isXeroInvoice) {
			return formatMessage({id: 'integrations.xero.xero_account'});
		}

		if (isQuickbooksInvoice) {
			return formatMessage({id: 'integrations.quickbooks.item_or_service'});
		}

		if (isEconomicInvoice) {
			return formatMessage({id: 'integrations.economic.product'});
		}

		return 'default';
	}, [isXeroInvoice, isQuickbooksInvoice, isEconomicInvoice]);

	useEffect(() => {
		setQuickbookSettingsCount(quickbookSettings.filter(item => item.checked).length);
	}, [quickbookSettings]);

	useEffect(() => {
		/*
		 * A merge, not a plain rebuild. This runs whenever the shared "apply to all lines" account
		 * dropdown changes, and it rebuilds from initialInvoiceEntries - the raw Relay entries -
		 * which carry none of the line's own Xero selections. Rebuilding outright would clear the
		 * Item and tracking values on every line. The account and its tax are deliberately still
		 * overwritten: that is what the shared dropdown is for.
		 */
		setInvoiceRows(previousRows =>
			carryXeroSelections(
				initialInvoiceEntries.map(entry =>
					mapInvoiceEntryToRow(entry, selectedXeroAccount, selectedQuickbooksProductService, selectedEconomicProduct)
				) || [],
				previousRows
			)
		);
	}, [selectedXeroAccount, selectedQuickbooksProductService, selectedEconomicProduct]);

	useEffect(() => {
		if (billTo) {
			setLoading(true);

			if (isXeroInvoice) {
				/*
				 * Only off the flag. `fetchXeroAccounts` uses the pre-flag route and feeds the
				 * invoice-level dropdown, which is hidden under the flag - so under it this was a
				 * live Xero call on every modal open, feeding a control nobody sees, while still
				 * being the only thing that cleared `loading`. `getXeroAccounts` calls `.json()`
				 * and `.map` on any non-403 response, so a 429 or a non-array body threw and left
				 * the modal loading for ever.
				 */
				if (!useLinesTable) {
					fetchXeroAccounts(billTo, (accounts: XeroAccountItem[]) => {
						setXeroAccounts(accounts);
						setLoading(false);
					});
				}

				if (useLinesTable) {
					// Stamped with the organisation asked for: the effect reruns on billTo, and an
					// earlier answer landing last would show one organisation's configuration
					// while the export posts to another.
					const tenantId = billTo.billFrom?.externalId;
					requestedTenant.current = tenantId;
					showXeroLineConfig(EMPTY_LINE_CONFIG);
					setXeroLineConfigLoading(true);
					fetchXeroLineConfig(tenantId).then(config => {
						/*
						 * Inside the guard, so a response for an organisation the user has moved
						 * away from cannot open the modal for the one now loading.
						 * `fetchXeroLineConfig` never rejects, so the current request always
						 * resolves and always reaches this.
						 */
						if (requestedTenant.current === tenantId) {
							showXeroLineConfig(config, tenantId);
							setXeroLineConfigLoading(false);
							// Under the flag this read replaces fetchXeroAccounts as what opens
							// the modal, so it is what clears `loading`.
							setLoading(false);
						}
					});
				}
			} else if (isQuickbooksInvoice) {
				setQuickbooksTaxCodesFailed(false);
				setQuickbooksProductsFailed(false);
				setQuickbooksClassesFailed(false);
				setQuickbooksListsLoaded(false);
				fetchQuickbooksData(
					(taxCodes: TaxCode[]) => setQuickbooksTaxCodes(taxCodes),
					(taxCode: TaxCode | null) => setSelectedQuickbooksCode(taxCode),
					(productsServices: QuickbookProductService[]) => setQuickbooksProductsServices(productsServices),
					({taxCodesFailed, productsFailed, classesFailed}) => {
						setQuickbooksTaxCodesFailed(taxCodesFailed);
						setQuickbooksProductsFailed(productsFailed);
						setQuickbooksClassesFailed(classesFailed);
						setQuickbooksListsLoaded(true);
						setLoading(false);
					},
					billTo.billFrom?.externalId || null,
					formatMessage({id: 'invoicing.no_tax'}),
					applyQuickbooksClassData
				);
			} else if (isEconomicInvoice) {
				fetchEconomicProducts(products => {
					setEconomicProducts(products);
					setLoading(false);
				});
			}
		}
	}, [billTo]);

	/*
	 * The legacy window's data load. Keyed on the realm, not the client: tax codes and products
	 * belong to a QuickBooks company, so moving between two clients of one realm keeps every pick,
	 * and moving to another realm starts the lines over - an item from realm A means nothing in B.
	 * Null while no client is chosen, and on the legacy Xero window, which has no realm.
	 */
	const clientRealmKey =
		exportTarget?.kind === 'CLIENT' && exportTarget.integration === INVOICE_INTEGRATIONS.QUICKBOOKS && exportTarget.clientId
			? exportTarget.realmId ?? ''
			: null;
	/*
	 * One number per load, not the realm: A -> B -> A sends two reads of realm A, and comparing realms
	 * would let the first answer through while the second is still out - clearing the loader, then
	 * replacing a code the user picked in the meantime.
	 */
	const requestSeq = useRef(0);

	useEffect(() => {
		if (exportTarget?.kind !== 'CLIENT' || exportTarget.integration !== INVOICE_INTEGRATIONS.QUICKBOOKS) return;
		// Bumped here too, so a read still out when the client is cleared lands on nothing.
		const seq = ++requestSeq.current;
		setQuickbooksListsLoaded(false);
		if (clientRealmKey === null) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setSelectedQuickbooksProductService(null);
		// Cleared here, not only by the load: a failed tax-code read sets no code, and the last
		// realm's code must not be exported to this one.
		setSelectedQuickbooksCode(null);
		setInvoiceRows(initialInvoiceEntries.map(entry => mapInvoiceEntryToRow(entry)));
		// The last company's lists too: its item ids are small per-company integers, so one picked
		// while this company loads could export as a different product here.
		setQuickbooksTaxCodes([]);
		setQuickbooksProductsServices([]);
		setQuickbooksTaxCodesFailed(false);
		setQuickbooksProductsFailed(false);
		// Classes are per organisation too: a class id from the last company could name another one here.
		setSelectedQuickbooksClass(null);
		setQuickbooksClasses([]);
		setQuickbooksClassesFailed(false);
		setQuickbooksClassAssignment('OFF');
		setQuickbooksImportedLists(false);
		// Each setter checks it still answers the latest load: a slow answer from one the user moved
		// away from must not overwrite the one now showing.
		const isCurrent = () => requestSeq.current === seq;
		fetchQuickbooksData(
			taxCodes => {
				if (isCurrent()) setQuickbooksTaxCodes(taxCodes);
			},
			taxCode => {
				if (isCurrent()) setSelectedQuickbooksCode(taxCode);
			},
			productsServices => {
				if (isCurrent()) setQuickbooksProductsServices(productsServices);
			},
			({taxCodesFailed, productsFailed, classesFailed}) => {
				if (!isCurrent()) return;
				setQuickbooksTaxCodesFailed(taxCodesFailed);
				setQuickbooksProductsFailed(productsFailed);
				setQuickbooksClassesFailed(classesFailed);
				setQuickbooksListsLoaded(true);
				setLoading(false);
			},
			clientRealmKey || null,
			formatMessage({id: 'invoicing.no_tax'}),
			classData => {
				if (isCurrent()) applyQuickbooksClassData(classData);
			}
		);
	}, [clientRealmKey]);

	/*
	 * The legacy Xero window's one read on open: the contacts, which resolve each client to its
	 * organisation. Fresh on every open, so a contact linked since the page loaded is seen. Xero not
	 * reachable at all closes the window for the inaccessible one, as the legacy window does. A read
	 * that failed keeps the window open with its lists shown as failed to load, and Export blocked:
	 * without contacts no client resolves to an organisation.
	 */
	useEffect(() => {
		if (!xeroClients) return;
		let unmounted = false;
		fetchXeroContacts().then(contacts => {
			if (unmounted) return;
			if (contacts === 'inaccessible') {
				closeModal();
				showModal({
					type: MODAL_TYPE.XERO_INACCESSIBLE,
					isAdminUser: hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS),
				});
				return;
			}
			if (contacts === 'failed') {
				showXeroLineConfig(FAILED_LINE_CONFIG);
				setXeroLineConfigLoading(false);
				setLoading(false);
				return;
			}
			setXeroContacts(contacts);
			setLoading(false);
		});
		return () => {
			unmounted = true;
		};
	}, []);

	// Seeds the legacy window's toggle once, from the first organisation a chosen client resolves to.
	useEffect(() => {
		const tenantId = xeroClientTarget?.tenantId;
		if (!xeroClientDefaultsShown || !tenantId || clientOpeningProjectInDescription !== null) return;
		const opening = xeroExportDefaultsFor(viewer.company?.billFroms, tenantId).projectInDescription;
		setClientOpeningProjectInDescription(opening);
		setProjectInDescription(opening);
	}, [xeroClientTarget?.tenantId]);

	/*
	 * The legacy Xero window's line configuration, for the organisation of the client now chosen.
	 * Moving to a client in another organisation clears the line picks - an account or item from one
	 * organisation means nothing in another - while a client in the same organisation keeps them. A
	 * line with no account takes the client's contact default tax.
	 */
	useEffect(() => {
		if (!xeroClientTarget || xeroContacts === null) return;
		const contact = xeroContactFor(xeroContacts, selectedXeroClient);
		/*
		 * T4-198 D4: on the new table a line's tax is its account's, or the client default laid over
		 * every line when the rows are read - never the contact's live rate. Gated here so the flag-off
		 * window keeps it, whichever way it is opened.
		 */
		const rate = useLinesTable ? undefined : contact?.DefaultTaxRate;
		const tenantId = contact?.TenantId;
		if (!tenantId) {
			requestedTenant.current = undefined;
			showXeroLineConfig(EMPTY_LINE_CONFIG);
			setXeroLineConfigLoading(false);
			return;
		}
		// Advanced outside the updater, which must stay pure.
		const previous = lastTenant.current;
		lastTenant.current = nextTenantId(previous, tenantId);
		// Stamped with the organisation asked for, so a slower answer for one the user left is dropped.
		requestedTenant.current = tenantId;
		setInvoiceRows(rows => {
			const loaded = nextLinesOnTenantLoad(previous, tenantId, rows, rate ?? undefined).lines;
			// Cleared lines get a new price and tax, so their totals are stated again whatever the rate.
			const restated = loaded === rows ? rows : withRestatedMoney(loaded);
			// D4: the new table never takes the contact's rate, so it isn't applied at all there.
			return useLinesTable ? restated : withContactTaxRate(restated, rate);
		});
		const cached = cachedXeroLineConfig(tenantId);
		if (cached) {
			showXeroLineConfig(cached, tenantId);
			setXeroLineConfigLoading(false);
			return;
		}
		showXeroLineConfig(EMPTY_LINE_CONFIG);
		setXeroLineConfigLoading(true);
		fetchXeroLineConfig(tenantId).then(config => {
			if (requestedTenant.current === tenantId) {
				showXeroLineConfig(config, tenantId);
				setXeroLineConfigLoading(false);
			}
		});
	}, [xeroContacts, selectedClientId]);

	const getMappedInvoiceRows = (
		row,
		xeroAccount: XeroAccountItem | null,
		productService: QuickbookProductService | null,
		economicProduct: EconomicProduct | null
	) => {
		return (
			invoiceRows.map(entry => {
				if (entry.id === row.id) {
					return mapInvoiceEntryToRow(entry, xeroAccount, productService, economicProduct);
				}

				return entry;
			}) || []
		);
	};

	const selectExpenseXeroAccount = (row, xeroAccount: XeroAccountItem) => {
		setInvoiceRows(getMappedInvoiceRows(row, xeroAccount, null, null));
	};

	const selectQuickBooksProductService = (row, productService: QuickbookProductService) => {
		setInvoiceRows(getMappedInvoiceRows(row, null, productService, null));
	};

	// Undefined is "None": the line goes without a class.
	const selectQuickbooksLineClass = (row: InvoiceEntryRow, classId: string | undefined) => {
		setInvoiceRows(rows =>
			rows.map(candidate => (candidate.id === row.id ? {...candidate, quickbooksClass: classId} : candidate))
		);
	};

	const selectEconomicProduct = (row, product: EconomicProduct) => {
		setInvoiceRows(getMappedInvoiceRows(row, null, null, product));
	};

	useEffect(() => {
		/*
		 * The legacy Xero window also waits for its organisation's configuration, the one it shows,
		 * and blocks a line holding an account that configuration cannot resolve.
		 */
		if (xeroClientTarget) {
			setIsReadyForExport(
				!(
					isXeroExportBlocked({
						exporting: saving,
						hasClient: !!xeroClientTarget.clientId,
						tenantId: xeroClientTarget.tenantId,
						lines: invoiceRows,
						alreadyExported: !!invoice?.xeroId,
					}) ||
					xeroLineConfigLoading ||
					isConfigStaleForClient(requestedTenant.current, xeroClientTarget.tenantId) ||
					hasUnresolvableAccount(invoiceRows, xeroLineConfig.accounts)
				)
			);
			return;
		}
		// The legacy window has nowhere to export to until a client is chosen.
		const hasTarget = !(exportTarget?.kind === 'CLIENT' && !exportTarget.clientId);
		// The new line table waits for an active account on every line, as the legacy Xero window does.
		if (isXeroInvoice && useLinesTable) {
			setIsReadyForExport(hasTarget && everyLineHasActiveAccount(invoiceRows, xeroLineConfig.accounts));
			return;
		}
		setIsReadyForExport(
			hasTarget &&
				invoiceRows.every(row => {
					if (isXeroInvoice) {
						return !!row.xeroAccount;
					}

					if (isQuickbooksInvoice) {
						return !!row.quickbooksItem;
					}

					if (isEconomicInvoice) {
						return !!row.economicProduct;
					}

					return false;
				})
		);
	}, [invoiceRows, exportTarget, xeroLineConfig, xeroLineConfigLoading, saving]);

	const onSave = () => {
		if (!invoice || !exportTarget) return;
		if (exportTarget.kind === 'CLIENT' && !exportTarget.clientId) return;
		// Worked out before any state changes: a target with no export behind it must not leave the
		// window loading.
		const exportBranch =
			isXeroInvoice && (billTo || xeroClientTarget?.tenantId)
				? INVOICE_INTEGRATIONS.XERO
				: isQuickbooksInvoice
				? INVOICE_INTEGRATIONS.QUICKBOOKS
				: isEconomicInvoice && billTo
				? INVOICE_INTEGRATIONS.ECONOMIC
				: null;
		if (!exportBranch) return;
		setLoading(true);
		setSaving(true);
		setExportError(null);

		const onSuccess = () => {
			setLoading(false);
			setSaving(false);
			closeModal();
		};

		/*
		 * A finished export that FAILED: stop the spinner, but leave the modal open.
		 *
		 * Closing on failure discards every item, account, tax and tracking pick the user made,
		 * with no way back but doing it all again - and the failures are ordinary and
		 * recoverable: an archived Xero contact, an invoice already linked, a token that needs
		 * reconnecting. The export writes nothing to the invoice unless Xero accepted it, so
		 * the modal the user is looking at is still exactly what they would retry.
		 */
		const onExportFailed = (reason?: string | null) => {
			setLoading(false);
			setSaving(false);
			// Null when the service gave nothing a user can act on - see exportFailureMessage.
			setExportError(reason || formatMessage({id: 'invoicing.not_exported'}));
		};

		const onError: OnErrorType = (error, onFailure) => {
			// Workaround. Old error modal is shown behind new design-system modals - so we have to close it first.
			onSuccess();
			onFailure(error);
		};
		if (exportBranch === INVOICE_INTEGRATIONS.XERO) {
			onExportXeroInvoice({
				intl,
				onSuccess,
				onExportFailed,
				// A Xero export only ever reaches here with the bill-to or the legacy window's Xero client.
				target: exportTarget as XeroExportTarget,
				projectInDescription,
				// The client default's code on every line when it applies (T4-198).
				invoiceRows: withDefaultTax(invoiceRows, appliedDefault),
				invoice,
				onError,
				useLinesTable,
			});
		} else if (exportBranch === INVOICE_INTEGRATIONS.QUICKBOOKS) {
			exportInvoiceToQuickbooksOnline({
				intl,
				onSuccess,
				target: exportTarget,
				invoice,
				invoiceRows,
				quickbookSettings,
				taxCode: selectedQuickbooksCode,
				importedLists: quickbooksImportedLists,
				classAssignment: quickbooksClassAssignment,
				selectedClass: selectedQuickbooksClass,
				priceFromProduct,
				onError,
			});
		} else if (exportBranch === INVOICE_INTEGRATIONS.ECONOMIC && billTo) {
			exportInvoiceToEconomics({
				intl,
				onSuccess,
				billTo,
				invoice,
				invoiceRows,
				onError,
			});
		}
	};

	const getProjectIdRenderProps = row => {
		return {
			color: row.projectColor,
			text: row.customProjectId,
		};
	};

	const getExportToName = () => {
		if (isXeroInvoice) {
			return 'Xero';
		}

		if (isQuickbooksInvoice) {
			return 'QuickBooks Online';
		}

		if (isEconomicInvoice) {
			return 'E-conomic';
		}

		return '';
	};

	// For a flip and a reset alike: the tax code is cleared whenever customer default tax changes,
	// either way, so the user picks it again.
	const applyQuickbookSettings = (next: QuickbookSetting[]) => {
		const taxChanged =
			isQuickbookSettingOn(next, QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX) !==
			isQuickbookSettingOn(quickbookSettings, QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX);
		setQuickbookSettings(next);
		if (qboNewBody && taxChanged) {
			setSelectedQuickbooksCode(null);
		}
	};

	const onQuickbookSettingChange = (id: QuickbooksSettingId) =>
		applyQuickbookSettings(toggleQuickbookSetting(quickbookSettings, id, qboNewBody));

	// `pointerType` is react-aria's: 'mouse' | 'pen' | 'touch' | 'keyboard' | 'virtual'.
	const onResetQuickbookSettings = (pointerType: string) => {
		if (!openingDefaults) return;
		applyQuickbookSettings(getInitialQuickbookSettings(intl, openingDefaults));
		// The button goes once nothing differs. From the keyboard or a screen reader, keep the user on the
		// options it reset; after a click, a focused checkbox would only show a focus border nobody asked for.
		if (pointerType === 'keyboard' || pointerType === 'virtual') {
			optionGridRef.current?.querySelector<HTMLElement>('[role="checkbox"]')?.focus();
		}
	};

	const onResetProjectInDescription = (pointerType: string) => {
		if (projectInDescriptionDefault === null) return;
		setProjectInDescription(projectInDescriptionDefault);
		// As for the QuickBooks options: after a keyboard or screen-reader press the button goes, so keep the
		// user on the checkbox it reset; after a click, a focused checkbox would only show an unasked-for border.
		if (pointerType === 'keyboard' || pointerType === 'virtual') {
			projectInDescriptionRef.current?.querySelector<HTMLElement>('[role="checkbox"]')?.focus();
		}
	};

	/*
	 * The design-system Checkbox takes its label as a plain string and gives the box its own id, so the change
	 * dot cannot go inside its label and no outside <label htmlFor> can reach it. The label is therefore drawn
	 * beside an unlabelled Checkbox: the box carries the name itself through aria-label (which also replaces
	 * the Checkbox's built-in "Select row"), the drawn text is aria-hidden so it is not read twice, and
	 * clicking the text toggles the box and moves focus to it, as clicking its label would.
	 */
	const renderProjectInDescriptionWithDefault = () => {
		const label = formatMessage({id: 'invoicing.xero.project_in_description'});
		const [labelHead, labelTail] = splitLastWord(label);
		const hintId = 'export-default-hint-project-in-description';
		const toggle = () => setProjectInDescription(!projectInDescription);
		const toggleFromLabel = () => {
			toggle();
			projectInDescriptionRef.current?.querySelector<HTMLElement>('[role="checkbox"]')?.focus();
		};
		return (
			<FlexColumn gap={'s'}>
				<FlexRow gap={'m'}>
					<div ref={projectInDescriptionRef} style={{display: 'flex'}}>
						<Checkbox
							checked={projectInDescription}
							onChange={toggle}
							disabled={projectInDescriptionDisabled}
							aria-label={label}
							aria-describedby={projectInDescriptionChanged ? hintId : undefined}
						/>
					</div>
					{/*
					 * The right padding is the room ChangedFromDefaultDot takes past the last word (its width plus
					 * its gap): the dot takes no space on the line, so without it a label that fills the row would
					 * push the dot past the edge.
					 */}
					<Label
						aria-hidden={true}
						onClick={projectInDescriptionDisabled ? undefined : toggleFromLabel}
						style={{
							overflowWrap: 'anywhere',
							paddingRight: '12px',
							cursor: projectInDescriptionDisabled ? undefined : 'pointer',
						}}
					>
						{labelHead}
						{/* The last word and the dot stay together, so the dot sits right after the label. */}
						<span style={{display: 'inline-block', maxWidth: '100%', overflowWrap: 'anywhere'}}>
							{labelTail}
							<ChangedFromDefaultDot visible={projectInDescriptionChanged} />
						</span>
					</Label>
				</FlexRow>
				{projectInDescriptionChanged && projectInDescriptionDefault !== null && (
					<>
						<CompanyDefaultHint id={hintId} defaultValue={projectInDescriptionDefault} />
						{/* A row, so the column does not stretch the button across the window. */}
						<FlexRow>
							<ResetToCompanyDefaultsButton onReset={onResetProjectInDescription} disabled={loading} />
						</FlexRow>
					</>
				)}
			</FlexColumn>
		);
	};

	const getDropdownColumnItems = () => {
		if (isXeroInvoice) {
			return xeroAccounts;
		}

		if (isQuickbooksInvoice) {
			return quickbooksProductsServices;
		}

		if (isEconomicInvoice) {
			return economicProducts;
		}

		return [];
	};

	/*
	 * Why Export is disabled, when it is disabled because Xero already holds the invoice.
	 *
	 * The readiness tooltip below answers "what do I still have to do"; this answers "why can I not
	 * try again", which is a different question and the one a user hits after a half-updated export.
	 * Without it the button simply goes dead with nothing said. Xero only - the gate reads
	 * `invoice.xeroId`, and QuickBooks and e-conomic have their own ids and their own modals' rules.
	 */
	const alreadyExportedTooltipContent =
		isXeroInvoice && invoice?.xeroId ? (
			<FlexColumn>
				<SubHeading>
					<FormattedMessage id={'invoicing.xero.export_done_tooltip.title'} />
				</SubHeading>
				<Text>
					<FormattedMessage id={'invoicing.xero.export_done_tooltip.description'} />
				</Text>
			</FlexColumn>
		) : undefined;

	// The legacy window with no client chosen: the client is the missing step, not a product.
	const awaitingClientTooltipContent =
		exportTarget?.kind === 'CLIENT' && !exportTarget.clientId ? (
			<SubHeading>
				<FormattedMessage id={'settings_finance.select_client'} />
			</SubHeading>
		) : undefined;

	// A QuickBooks list that failed: say that, rather than ask for a pick the user cannot make.
	const failedListTooltipContent = exportBlockedByTaxCodes ? (
		<SubHeading>
			<FormattedMessage id={'invoicing.quickbooks.tax_codes_load_failed'} />
		</SubHeading>
	) : !isReadyForExport && isQuickbooksInvoice && quickbooksProductsFailed ? (
		<SubHeading>
			<FormattedMessage id={'invoicing.quickbooks.products_load_failed'} />
		</SubHeading>
	) : undefined;

	// Asked for last: a client, a list that loaded and a product on every line all come first.
	const taxCodeRequiredTooltipContent = quickbooksTaxCodeMissing ? (
		<FlexColumn>
			<SubHeading>
				<FormattedMessage id={'invoicing.quickbooks.tax_code_required_tooltip.title'} />
			</SubHeading>
			<Text>
				<FormattedMessage id={'invoicing.quickbooks.tax_code_required_tooltip.description'} />
			</Text>
		</FlexColumn>
	) : undefined;

	const readinessTooltipContent = isReadyForExport
		? failedListTooltipContent ?? taxCodeRequiredTooltipContent
		: awaitingClientTooltipContent ??
		  failedListTooltipContent ?? (
				<FlexColumn>
					<SubHeading>
						<FormattedMessage
							id={
								isXeroInvoice
									? 'invoicing.xero.export_disabled_tooltip.title'
									: isEconomicInvoice
									? 'invoicing.economic.export_disabled_tooltip.title'
									: 'invoicing.quickbooks.export_disabled_tooltip.title'
							}
						/>
					</SubHeading>
					<Text>
						<FormattedMessage
							id={
								isXeroInvoice
									? 'invoicing.xero.export_disabled_tooltip.description'
									: isEconomicInvoice
									? 'invoicing.economic.export_disabled_tooltip.description'
									: 'invoicing.quickbooks.export_disabled_tooltip.description'
							}
						/>
					</Text>
				</FlexColumn>
		  );

	// The Xero link wins: it is the reason the button is dead even on a line that IS ready.
	const disabledTooltipContent = alreadyExportedTooltipContent ?? readinessTooltipContent;
	return (
		<Modal size={Modal.SIZE.EXTRA_EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>
				<Heading size={useLinesTable ? 3 : 4}>
					<FormattedMessage id={'invoicing.export_to'} values={{systemName: getExportToName()}} />
				</Heading>
			</Modal.Title>

			{/*
			 * Outside `Modal.Content`, deliberately.
			 *
			 * That element is the scroll container - `max-height: calc(100vh - 300px); overflow-y:
			 * auto` - while the Export button sits below it in the footer. As the first child inside
			 * it, the reason was off-screen for the user most likely to need it: the one who scrolled
			 * a long line table to reach Export. They saw the spinner stop and nothing else, which is
			 * the symptom this whole change exists to remove.
			 *
			 * The padding matches `globalSpacing['600']` (24px), which `Modal.Title` and
			 * `Modal.Content` both use, so the reason lines up with the dialog's other text. The token
			 * is not exported from the design system's package root, or it would be used by name.
			 */}
			{exportError ? (
				<div
					data-cy={'export-failure-reason'}
					role={'alert'}
					/*
					 * Scrolls rather than growing: the reason comes from Xero and has no length limit,
					 * and this sits OUTSIDE the content's own scroll area, so an unbounded one pushes
					 * the dialog past the viewport and takes the footer buttons with it.
					 *
					 * `min(20vh, 120px)`, not `20vh`. `Modal.Content` already claims
					 * `calc(100vh - 300px)`, leaving roughly 150px for the title, this and the footer -
					 * so on anything taller than about 750px a flat 20vh is more than the room left.
					 * Measured in Chromium: at 1366x768 the bottom of Export landed at 769px, and at
					 * 1920x1080 the dialog lost its top 58px.
					 */
					style={{padding: '0 24px', maxHeight: 'min(20vh, 120px)', overflowY: 'auto'}}
				>
					<Text color={'error'}>{exportError}</Text>
				</div>
			) : null}

			<Modal.Content>
				<FlexColumn gap={'xxl'}>
					<ExportHeaderRow
						gap={'l'}
						alignItems={'start'}
						$fixedFieldWidths={qboNewBody && exportTarget?.kind === 'BILL_TO'}
						$xeroPathways={useLinesTable && exportTarget?.kind === 'BILL_TO'}
					>
						{exportTarget?.kind === 'CLIENT' ? (
							<>
								<FlexColumn>
									{/*
									 * Searchable, as the old window's client picker was: a company can have hundreds of
									 * linked clients, and typing filters by substring.
									 *
									 * QuickBooks: `saving`, not `loading`: a slow company must not trap the user on the
									 * client they are moving away from. The per-load check in the load effect is what makes
									 * switching mid-load safe. Xero: also disabled until the contacts read settles, so the
									 * "(Organisation)" labels never replace text the user has typed.
									 */}
									<SubHeading>
										<span id={CLIENT_LABEL_ID}>
											<FormattedMessage id={'common.client'} />
										</span>
									</SubHeading>
									<ComboBox
										aria-labelledby={CLIENT_LABEL_ID}
										defaultItems={clientComboItems}
										value={selectedClientId}
										// Null when the text is cleared; the window has no "no client" choice.
										onChange={(key: string | null) => {
											if (key != null) setSelectedClientId(String(key));
										}}
										/*
										 * On the Xero path, closed until the contacts read has settled: nothing typed
										 * before then can be lost, and the unfocused field picks up the chosen client's
										 * "(Organisation)" label when the labelled list arrives.
										 */
										disabled={saving || (!!xeroClients && loading)}
										placeholder={
											formatMessage({id: 'common.select'}) + ' ' + formatMessage({id: 'common.client'})
										}
										helperText={
											quickbooksCustomers && quickbooksCustomers.length === 0
												? formatMessage({id: 'invoicing.quickbooks.no_linked_clients'})
												: xeroClients && xeroClients.length === 0
												? formatMessage({id: 'invoicing.xero.no_linked_clients'})
												: undefined
										}
									>
										{(option: {value: string; label: string}) => (
											<ComboBox.Item key={option.value} textValue={option.label}>
												<Text>{option.label}</Text>
											</ComboBox.Item>
										)}
									</ComboBox>
								</FlexColumn>
								{/* Judged against the chosen client's organisation: a dash until xero/contacts names it. */}
								<ExportDefaultTaxRateField
									show={showDefaultTaxRate}
									rate={defaultTaxInput.rate}
									organisation={defaultTaxInput.organisation}
								/>
							</>
						) : (
							<>
								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'common.client'} />
									</SubHeading>

									<Text ellipsis tooltipOnEllipsis>
										{client?.name}
									</Text>
								</FlexColumn>

								<ExportDefaultTaxRateField
									show={showDefaultTaxRate}
									rate={defaultTaxInput.rate}
									organisation={defaultTaxInput.organisation}
								/>

								<FlexColumn>
									<FlexRow alignItems={'center'}>
										<SubHeading>
											<FormattedMessage id={'settings_finance.bill_from_entity'} />
										</SubHeading>

										<IconTooltip>
											<IconTooltip.Item
												title={formatMessage({id: 'settings_finance.bill_from_entity'})}
												details={formatMessage({
													id: 'invoicing.export.bill_from_entity_tooltip_details',
												})}
											/>
										</IconTooltip>
									</FlexRow>

									<Text ellipsis tooltipOnEllipsis>
										{billTo?.billFrom?.name}
									</Text>
								</FlexColumn>

								<FlexColumn>
									<FlexRow alignItems={'center'}>
										<SubHeading>
											<FormattedMessage id={'settings_finance.bill_to_entity'} />
										</SubHeading>

										<IconTooltip>
											<IconTooltip.Item
												title={formatMessage({id: 'settings_finance.bill_to_entity'})}
												details={formatMessage({id: 'invoicing.export.bill_to_entity_tooltip_details'})}
											/>
										</IconTooltip>
									</FlexRow>

									<Text ellipsis tooltipOnEllipsis>
										{billTo?.name}
									</Text>
								</FlexColumn>
							</>
						)}

						{/*
						 * Hidden under the flag: the line's own Account column replaces it, and the design
						 * drops the invoice-level picker. Leaving it on screen would be actively unsafe -
						 * it is fed by the pre-flag route, which returns EVERY account class unfiltered, so
						 * an expense account picked here satisfies the Export gate (!!row.xeroAccount) while
						 * the filtered column cannot resolve it and every line reads "Select account".
						 */}
						{isXeroInvoice && !useLinesTable && (
							<FlexColumn>
								<SubHeading>
									<FormattedMessage id={'integrations.xero.xero_account'} />

									<DropdownList>
										<DropdownList.Trigger>
											<ButtonDropdownField disabled={loading} isDefaultLabel={!selectedXeroAccount}>
												{selectedXeroAccount
													? selectedXeroAccount.label
													: formatMessage({id: 'common.select'}) +
													  ' ' +
													  formatMessage({id: 'integrations.xero.xero_account'})}
											</ButtonDropdownField>
										</DropdownList.Trigger>
										{xeroAccounts?.length > 0 && (
											<DropdownList.Content offset={10}>
												{xeroAccounts.map((xeroAccount, index) => (
													<DropdownList.Item
														key={`xero-${index}`}
														onClick={() => setSelectedXeroAccount(xeroAccount)}
													>
														{xeroAccount.label}
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										)}
									</DropdownList>
								</SubHeading>
							</FlexColumn>
						)}

						{isQuickbooksInvoice && (
							<>
								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'integrations.quickbooks.tax_code'} />

										{renderQuickbooksTaxFieldHover(
											<DropdownList>
												<DropdownList.Trigger>
													<ButtonDropdownField
														disabled={loading || customerDefaultTaxOn}
														isDefaultLabel={quickbooksTaxFieldIsPlaceholder}
													>
														{quickbooksNoTaxCodesShown
															? formatMessage({id: 'invoicing.quickbooks.no_tax_codes_imported'})
															: customerDefaultTaxOn
															? quickbooksCustomerDefault?.label ??
															  formatMessage({
																	id: quickbooksAutomatedSalesTax
																		? 'invoicing.quickbooks.calculated_by_quickbooks'
																		: 'invoicing.quickbooks.no_default_set',
															  })
															: quickbooksTaxCodesFailed
															? formatMessage({id: 'invoicing.quickbooks.tax_codes_load_failed'})
															: selectedQuickbooksCode
															? selectedQuickbooksCode.label
															: formatMessage({
																	id: qboNewBody
																		? 'invoicing.quickbooks.select_tax_code'
																		: 'invoicing.no_tax',
															  })}
													</ButtonDropdownField>
												</DropdownList.Trigger>
												{quickbooksTaxCodes?.length > 0 && (
													<DropdownList.Content offset={10}>
														{/* Nothing imported still offers "No tax": a QuickBooks company with no tax codes must be able to export. */}
														{quickbooksNoTaxCodesImported && (
															<DropdownList.Item disabled onClick={noop}>
																{formatMessage({
																	id: 'invoicing.quickbooks.no_tax_codes_imported',
																})}
															</DropdownList.Item>
														)}
														{quickbooksTaxCodes.map((taxCode, index) => (
															<DropdownList.Item
																key={`tax-code-${index}`}
																onClick={() => setSelectedQuickbooksCode(taxCode)}
															>
																{taxCode.label}
															</DropdownList.Item>
														))}
													</DropdownList.Content>
												)}
											</DropdownList>
										)}
									</SubHeading>
								</FlexColumn>

								{qboNewBody && quickbooksClassAssignment === 'TRANSACTION' && (
									<FlexColumn>
										<SubHeading>
											<FormattedMessage id={'invoicing.quickbooks.class'} />

											<DropdownList>
												<DropdownList.Trigger>
													<ButtonDropdownField
														disabled={loading || quickbooksClassesFailed}
														isDefaultLabel={!selectedQuickbooksClass}
													>
														{selectedQuickbooksClass
															? selectedQuickbooksClass.label
															: quickbooksClassPlaceholder}
													</ButtonDropdownField>
												</DropdownList.Trigger>
												{!quickbooksClassesFailed && (
													<DropdownList.Content offset={10}>
														{quickbooksClassesUnavailable ? (
															<DropdownList.Item disabled onClick={noop}>
																{quickbooksClassPlaceholder}
															</DropdownList.Item>
														) : (
															[
																<DropdownList.Item
																	key="class-none"
																	onClick={() => setSelectedQuickbooksClass(null)}
																>
																	{formatMessage({id: 'common.none'})}
																</DropdownList.Item>,
																...quickbooksClasses.map(quickbooksClass => (
																	<DropdownList.Item
																		key={`class-${quickbooksClass.value}`}
																		onClick={() =>
																			setSelectedQuickbooksClass(quickbooksClass)
																		}
																	>
																		{quickbooksClass.label}
																	</DropdownList.Item>
																)),
															]
														)}
													</DropdownList.Content>
												)}
											</DropdownList>
										</SubHeading>
									</FlexColumn>
								)}

								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'integrations.quickbooks.items_and_services'} />

										<DropdownList>
											<DropdownList.Trigger>
												<ButtonDropdownField
													disabled={loading}
													width={'320px'}
													isDefaultLabel={!selectedQuickbooksProductService}
												>
													{selectedQuickbooksProductService
														? selectedQuickbooksProductService.label
														: quickbooksNoProductsImported
														? formatMessage({id: 'invoicing.quickbooks.no_products_imported'})
														: quickbooksProductsFailed
														? formatMessage({id: 'invoicing.quickbooks.products_load_failed'})
														: formatMessage({id: 'common.select'}) +
														  ' ' +
														  formatMessage({id: 'integrations.quickbooks.items_and_services'})}
												</ButtonDropdownField>
											</DropdownList.Trigger>
											{quickbooksNoProductsImported && (
												<DropdownList.Content offset={10}>
													<DropdownList.Item disabled onClick={noop}>
														{formatMessage({id: 'invoicing.quickbooks.no_products_imported'})}
													</DropdownList.Item>
												</DropdownList.Content>
											)}
											{quickbooksProductsServices?.length > 0 && (
												<DropdownList.Content offset={10}>
													{quickbooksProductsServices.map((productService, index) => (
														<DropdownList.Item
															key={`product-service-${index}`}
															onClick={() => setSelectedQuickbooksProductService(productService)}
														>
															{productService.label}
														</DropdownList.Item>
													))}
												</DropdownList.Content>
											)}
										</DropdownList>
									</SubHeading>
								</FlexColumn>
							</>
						)}

						{isEconomicInvoice && (
							<FlexColumn>
								<SubHeading>
									<FormattedMessage id={'integrations.economic.product'} />

									<DropdownList>
										<DropdownList.Trigger>
											<ButtonDropdownField disabled={loading} isDefaultLabel={!selectedEconomicProduct}>
												{selectedEconomicProduct
													? selectedEconomicProduct.label
													: formatMessage({id: 'common.select'}) +
													  ' ' +
													  formatMessage({id: 'integrations.economic.product'})}
											</ButtonDropdownField>
										</DropdownList.Trigger>
										{economicProducts?.length > 0 && (
											<DropdownList.Content offset={10}>
												{economicProducts.map((product, index) => (
													<DropdownList.Item
														key={`economic-product-${index}`}
														onClick={() => setSelectedEconomicProduct(product)}
													>
														{product.label}
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										)}
									</DropdownList>
								</SubHeading>
							</FlexColumn>
						)}
					</ExportHeaderRow>

					{isQuickbooksInvoice && !qboNewBody && (
						<FlexRow>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdown emphasis={'medium'} style={{width: 'fit-content'}}>
										<FormattedMessage
											id={'invoicing.quickbooks.quickbook_settings'}
											values={{count: quickbookSettingsCount}}
										/>
									</ButtonDropdown>
								</DropdownList.Trigger>
								<DropdownList.Content offset={10} align={'start'}>
									{quickbookSettings.map(setting => (
										<DropdownList.CheckboxItem
											key={`quickbook-setting-${setting.id}`}
											onChange={() => onQuickbookSettingChange(setting.id)}
											checked={setting.checked}
											disabled={setting.disabled}
										>
											{setting.name}
										</DropdownList.CheckboxItem>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexRow>
					)}

					{qboNewBody && (
						<FlexColumn gap={'s'}>
							{/*
							 * Five columns, one per option: a row of an unlabelled Checkbox and its drawn label (the
							 * changed dot after the label's last word), then the "Company default" line under it (T4-223,
							 * spec 050 D1-D2; the pattern of renderProjectInDescriptionWithDefault). The design-system
							 * Checkbox cannot hold the dot inside its own label, so the box is named through aria-label
							 * (which also replaces its built-in "Select row"), the drawn label is aria-hidden so it is
							 * not read twice, and clicking it ticks the box and moves focus to it.
							 *
							 * A named group keeps the pair findable by name. While the option differs, the checkbox is
							 * described by its "Company default" line.
							 */}
							<div
								ref={optionGridRef}
								style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
									columnGap: '20px',
									alignItems: 'start',
								}}
							>
								{quickbookSettings.map(setting => {
									const changed = isChangedFromDefault(setting, openingDefaults);
									const hintId = `export-default-hint-${setting.id}`;
									const [labelHead, labelTail] = splitLastWord(setting.name);
									const optionDisabled = setting.disabled || saving;
									const tickFromLabel = (event: React.MouseEvent<HTMLElement>) => {
										onQuickbookSettingChange(setting.id);
										event.currentTarget
											.closest('[role="group"]')
											?.querySelector<HTMLElement>('[role="checkbox"]')
											?.focus();
									};
									return (
										<div key={`quickbook-setting-${setting.id}`} role={'group'} aria-label={setting.name}>
											<div style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
												<Checkbox
													checked={setting.checked}
													onChange={() => onQuickbookSettingChange(setting.id)}
													disabled={optionDisabled}
													aria-label={setting.name}
													aria-describedby={changed ? hintId : undefined}
												/>
												{/*
												 * The right padding is the room ChangedFromDefaultDot takes past the last word: the
												 * dot takes no space on the line. A word too long for the column breaks inside the
												 * word rather than spilling into the next column (T4-151).
												 */}
												<Label
													aria-hidden={true}
													onClick={optionDisabled ? undefined : tickFromLabel}
													style={{
														overflowWrap: 'anywhere',
														paddingRight: '12px',
														cursor: optionDisabled ? undefined : 'pointer',
													}}
												>
													{labelHead}
													{/* The last word and the dot stay together, so the dot sits right after the label. */}
													<span
														style={{
															display: 'inline-block',
															maxWidth: '100%',
															overflowWrap: 'anywhere',
														}}
													>
														{labelTail}
														<ChangedFromDefaultDot visible={changed} />
													</span>
												</Label>
											</div>
											{changed && openingDefaults && (
												<div style={{marginTop: '4px'}}>
													<CompanyDefaultHint
														id={hintId}
														defaultValue={openingDefaults[setting.id]}
													/>
												</div>
											)}
										</div>
									);
								})}
							</div>
							{anyChangedFromDefault && (
								// A row, so the column does not stretch the button across the window.
								<FlexRow>
									<ResetToCompanyDefaultsButton onReset={onResetQuickbookSettings} disabled={saving} />
								</FlexRow>
							)}
						</FlexColumn>
					)}

					{isXeroInvoice && !xeroDefaultsShown && (
						<Checkbox
							checked={projectInDescription}
							onChange={() => setProjectInDescription(!projectInDescription)}
							labelText={formatMessage({id: 'invoicing.xero.project_in_description'})}
							disabled={loading}
						/>
					)}

					{xeroDefaultsShown && renderProjectInDescriptionWithDefault()}

					{xeroNothingImported && (
						<XeroNothingImportedAlert
							canOpenConfiguration={hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS)}
							onOpenConfiguration={openXeroConfiguration}
						/>
					)}

					{xeroContactMissing && (
						<Alert
							type={Alert.TYPE.WARNING}
							text={formatMessage({id: 'integrations.xero.export.contact_not_found'})}
						/>
					)}

					{useLinesTable ? (
						<XeroExportLinesTable
							// The pathways window gives the project badge its own ID column; the legacy one shows it under Project.
							idHeader={exportTarget?.kind === 'BILL_TO' ? formatMessage({id: 'common.id'}) : ''}
							rows={toXeroLineRows(invoiceRows, projectInDescription, appliedDefault)}
							currency={invoice?.currency || ''}
							accounts={xeroLineConfig.accounts}
							items={xeroLineConfig.items}
							trackingCategories={xeroLineConfig.trackingCategories}
							onRowsChange={changed => setInvoiceRows(rows => applyXeroLineRows(rows, changed))}
							disabled={saving}
							canRepriceLines={hasPermission(PERMISSION_TYPE.INVOICE_UPDATE)}
							loading={xeroLineConfigLoading}
							failed={xeroLineConfig.failed}
							listsLoaded={xeroListsLoaded}
						/>
					) : (
						<Table data={tableRows} width={'100%'}>
							<Table.ColoredIdIconColumn
								accessorKey={'project.id'}
								header={formatMessage({id: 'common.id'})}
								renderProps={getProjectIdRenderProps}
							/>
							<Table.TextColumn accessorKey={'project.name'} header={formatMessage({id: 'common.project'})} />
							<Table.TextColumn
								accessorKey={'entryDescription'}
								width={'232px'}
								header={formatMessage({id: 'common.description'})}
							/>

							<Table.DropdownColumn
								header={dropdownColumnLabel}
								label={row => row.label || ''}
								defaultLabel={
									quickbooksNoProductsImported
										? formatMessage({id: 'invoicing.quickbooks.no_products_imported'})
										: formatMessage({id: 'common.select'}) + ' ' + dropdownColumnLabel
								}
								width={'180px'}
							>
								{quickbooksNoProductsImported
									? [
											// The column drops `disabled`, so the no-op is what keeps the message inert.
											<Table.DropdownColumn.ListItem key="no-products" disabled onClick={noop}>
												{formatMessage({id: 'invoicing.quickbooks.no_products_imported'})}
											</Table.DropdownColumn.ListItem>,
									  ]
									: getDropdownColumnItems().map((item, index) => (
											<Table.DropdownColumn.ListItem
												key={`row-dropdown-${index}`}
												onClick={row => {
													if (isXeroInvoice) {
														selectExpenseXeroAccount(row, item);
													} else if (isQuickbooksInvoice) {
														selectQuickBooksProductService(row, item);
													} else if (isEconomicInvoice) {
														selectEconomicProduct(row, item);
													}
												}}
												disabled={loading}
											>
												{item.label}
											</Table.DropdownColumn.ListItem>
									  ))}
							</Table.DropdownColumn>

							<Table.NumberColumn accessorKey={'quantity'} header={formatMessage({id: 'common.quantity'})} />
							<Table.CurrencyColumn
								currency={invoice?.currency || ''}
								accessorKey={'unitPrice'}
								header={formatMessage({id: 'common.unit_price'})}
								maximumFractionDigits={4}
							/>
							<Table.PercentageColumn
								accessorKey={'discountPercentage'}
								header={formatMessage({id: 'project_section.xero_modal_table_discount'})}
							/>
							{qboNewBody ? (
								<Table.TextColumn
									// A key no row sets, so the column draws only the icon slot's text.
									accessorKey={'quickbooksTaxCell'}
									header={formatMessage({id: 'common.tax'})}
									align={Table.Align.right}
									icon={renderQuickbooksLineTax}
								/>
							) : (
								<Table.PercentageColumn
									accessorKey={'taxPercentage'}
									header={formatMessage({id: 'common.tax'})}
								/>
							)}
							{qboNewBody && quickbooksClassAssignment === 'LINE' ? (
								<Table.DropdownColumn
									header={formatMessage({id: 'invoicing.quickbooks.class_column'})}
									label={(row: InvoiceEntryRow) =>
										quickbooksClasses.find(quickbooksClass => quickbooksClass.value === row.quickbooksClass)
											?.label || ''
									}
									defaultLabel={quickbooksClassPlaceholder}
									width={'160px'}
								>
									{quickbooksClassesUnavailable
										? [
												// The column drops `disabled`, so the no-op is what keeps the message inert.
												<Table.DropdownColumn.ListItem key="class-unavailable" onClick={noop}>
													{quickbooksClassPlaceholder}
												</Table.DropdownColumn.ListItem>,
										  ]
										: [
												<Table.DropdownColumn.ListItem
													key="class-none"
													onClick={(row: InvoiceEntryRow) =>
														selectQuickbooksLineClass(row, undefined)
													}
												>
													{formatMessage({id: 'common.none'})}
												</Table.DropdownColumn.ListItem>,
												...quickbooksClasses.map(quickbooksClass => (
													<Table.DropdownColumn.ListItem
														key={`class-${quickbooksClass.value}`}
														onClick={(row: InvoiceEntryRow) =>
															selectQuickbooksLineClass(row, quickbooksClass.value)
														}
													>
														{quickbooksClass.label}
													</Table.DropdownColumn.ListItem>
												)),
										  ]}
								</Table.DropdownColumn>
							) : undefined}
							<Table.CurrencyColumn
								currency={invoice?.currency || ''}
								accessorKey={'totalPrice'}
								header={formatMessage({id: 'expense_item_modal.total_price'})}
							/>
						</Table>
					)}

					<FlexRow alignItems={'end'} alignSelf={'end'}>
						<FlexColumn gap={'s'}>
							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'common.subtotal'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.subtotal, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'project_section.xero_modal_table_discount'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.discount, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'common.tax'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.tax, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<SubHeading>
									<FormattedMessage id={'common.total_amount'} />
								</SubHeading>
								<SubHeading>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.total, intl, noSpace)}
								</SubHeading>
							</FlexRow>
						</FlexColumn>
					</FlexRow>
				</FlexColumn>
			</Modal.Content>

			<Modal.Footer>
				{/*
				 * `invoice.xeroId` as well: the failure branch returns `invoiceId` so Relay refetches,
				 * and the failure that matters most is the half-updated one - Xero accepted, the link
				 * was written, and a later step threw. The modal stays open by design, so the user's
				 * picks survive, but a retry on an invoice Xero already holds is the one thing that
				 * must not be invited. The button that OPENS this modal is already hidden once the
				 * link exists, so nothing changes on the ordinary path.
				 */}
				<Modal.PrimaryFooterButton
					onPress={onSave}
					tooltipContent={disabledTooltipContent}
					disabled={
						!isReadyForExport || loading || !!invoice?.xeroId || exportBlockedByTaxCodes || quickbooksTaxCodeMissing
					}
				>
					{formatMessage({id: 'common.export'})}
				</Modal.PrimaryFooterButton>

				<Modal.SecondaryFooterButton onPress={closeModal} disabled={loading}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default createFragmentContainer(ExportInvoiceModal, exportInvoiceModalFragment);
